import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleNewsletter = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      setEmail('');
      setIsSubmitted(false);
    }, 3000);
  };

  const solutions = [
    t('footer.solution1'),
    t('footer.solution2'),
    t('footer.solution3'),
    t('footer.solution4')
  ];

  const company = [
    t('footer.company1'),
    t('footer.company2'),
    t('footer.company3'),
    t('footer.company4')
  ];

  const resources = [
    t('footer.resource1'),
    t('footer.resource2'),
    t('footer.resource3'),
    t('footer.resource4')
  ];

  return (
    <footer id="contact" className="relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/05d4fd81a25168c11a64fa0097c15bd2.jpg"
          alt="Footer Background"
          className="w-full h-full object-cover object-center"
        />
      </div>

      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a1a]/95 via-[#1a1a2e]/90 to-[#0a0a1a]/95"></div>
      </div>

      <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF6B6B]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#4ECDC4]/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-16 text-white">
        {/* Top Section */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16 pb-16 border-b border-white/10">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] flex items-center justify-center shadow-lg shadow-[#FF6B6B]/30">
                <i className="ri-luggage-cart-line text-white text-2xl"></i>
              </div>
              <span className="text-lg font-bold" style={{ fontFamily: 'Poppins, sans-serif' }}>
                AIR BAGGAGE<span className="text-[#FF6B6B]"> REPAIR SOLUTIONS</span>
              </span>
            </div>
            <p className="text-white/80 text-lg mb-4 max-w-md" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('footer.tagline')}
            </p>
            <p className="text-white/60 text-sm mb-6 max-w-md">
              {t('footer.description')}
            </p>
            <div className="flex gap-3">
              {[
                { icon: 'ri-linkedin-fill', color: 'from-[#0077B5] to-[#00A0DC]' },
                { icon: 'ri-facebook-fill', color: 'from-[#1877F2] to-[#42B72A]' },
                { icon: 'ri-twitter-x-fill', color: 'from-[#1DA1F2] to-[#14171A]' },
                { icon: 'ri-instagram-line', color: 'from-[#F58529] to-[#DD2A7B]' },
              ].map((social, index) => (
                <a
                  key={index}
                  href="#"
                  className={`w-11 h-11 bg-gradient-to-br ${social.color} rounded-xl flex items-center justify-center hover:scale-110 transition-transform cursor-pointer shadow-lg`}
                >
                  <i className={`${social.icon} text-white`}></i>
                </a>
              ))}
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 shadow-2xl">
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('footer.newsletterTitle')}
            </h3>
            <p className="text-white/70 mb-6">{t('footer.newsletterDesc')}</p>
            <form onSubmit={handleNewsletter} className="flex gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footer.newsletterPlaceholder')}
                className="flex-1 px-5 py-4 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:border-[#FF6B6B] transition-colors backdrop-blur-sm"
                required
              />
              <button
                type="submit"
                className="px-6 py-4 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] rounded-xl font-semibold hover:shadow-lg hover:shadow-[#FF6B6B]/40 transition-all cursor-pointer whitespace-nowrap"
                disabled={isSubmitted}
              >
                {isSubmitted ? (
                  <i className="ri-check-line text-xl"></i>
                ) : (
                  <i className="ri-send-plane-fill text-xl"></i>
                )}
              </button>
            </form>
            {isSubmitted && (
              <p className="text-[#4ECDC4] text-sm mt-3 flex items-center gap-2">
                <i className="ri-checkbox-circle-fill"></i>
                {t('footer.newsletterSuccess')}
              </p>
            )}
          </div>
        </div>

        {/* Notre équipe à votre service - New Section */}
        <div className="mb-16 pb-16 border-b border-white/10">
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('footer.teamTitle')}
            </h3>
            <p className="text-white/70 text-sm max-w-2xl mx-auto">
              {t('footer.teamDesc')}
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="rounded-3xl overflow-hidden shadow-2xl border border-white/20">
              <img
                src="https://static.readdy.ai/image/60b7a1bdc124301cce40c5bb19c672c9/dea0a49fb8b571dc79b53a8caaf07e48.jpeg"
                alt="Notre équipe à votre service - Bureau ABRS"
                title="Air Baggage Repair Solutions - Notre équipe professionnelle"
                className="w-full h-[400px] object-cover object-center"
              />
            </div>
          </div>
        </div>

        {/* Middle Section - Contact Info with Airport Background */}
        <div className="relative mb-16 pb-16 border-b border-white/10 -mx-6">
          {/* Airport Background */}
          <div className="relative rounded-3xl mx-6 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20international%20airport%20terminal%20interior%20at%20night%20with%20dramatic%20lighting%20glass%20walls%20reflections%20of%20runway%20lights%20sleek%20architecture%20warm%20amber%20and%20cool%20teal%20tones%20professional%20aviation%20atmosphere%20high%20end%20travel%20hub&width=1400&height=700&seq=footer-airport-contact-bg-002&orientation=landscape"
                alt="Airport terminal background"
                title="Air Baggage Repair Solutions - Présence internationale aéroports"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a1a]/85 via-[#1a1a2e]/80 to-[#0a0a1a]/90"></div>
            </div>

            <div className="relative z-10 px-8 py-12 md:px-12">
              {/* Section Title */}
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-5 py-2 mb-4 border border-white/20">
                  <i className="ri-global-line text-[#FF6B6B]"></i>
                  <span className="text-sm font-medium text-white/90">{t('footer.contactTitle')}</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {t('footer.teamTitle')}
                </h3>
              </div>

              {/* Phone Numbers Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
                {[
                  { flag: '🇧🇪', country: t('footer.contactBelgium'), phone: '+32 10 39 83 83', tel: '+3210398383' },
                  { flag: '🇨🇭', country: 'Suisse', phone: '+41 22 562 42 48', tel: '+41225624248' },
                  { flag: '🇲🇦', country: t('footer.contactMorocco'), phone: '+212 5 31 18 84 66', tel: '+212531188466' },
                  { flag: '🇺🇸', country: 'USA', phone: '+1 832 295 0203', tel: '+18322950203' },
                  { flag: '🇦🇪', country: 'UAE', phone: '+971 58 514 6914', tel: '+971585146914' },
                ].map((item, index) => (
                  <a
                    key={index}
                    href={`tel:${item.tel}`}
                    className="group bg-white/8 backdrop-blur-md border border-white/15 rounded-2xl p-5 hover:bg-white/15 hover:border-[#FF6B6B]/40 transition-all duration-300 cursor-pointer hover:scale-[1.03] hover:shadow-lg hover:shadow-[#FF6B6B]/10"
                  >
                    <div className="text-3xl mb-3">{item.flag}</div>
                    <p className="text-white font-semibold text-sm mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {item.country}
                    </p>
                    <div className="flex items-center gap-1.5 text-white/70 group-hover:text-[#FF6B6B] transition-colors">
                      <i className="ri-phone-line text-xs w-4 h-4 flex items-center justify-center"></i>
                      <span className="text-xs">{item.phone}</span>
                    </div>
                  </a>
                ))}
              </div>

              {/* Email Row */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="mailto:Belgique@airbrs.com"
                  className="group flex items-center gap-3 bg-white/8 backdrop-blur-md border border-white/15 rounded-2xl px-6 py-4 hover:bg-white/15 hover:border-[#FF6B6B]/40 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] flex items-center justify-center shadow-lg">
                    <i className="ri-mail-line text-white"></i>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">{t('footer.contactEmailBelgium')}</p>
                    <p className="text-white font-medium text-sm group-hover:text-[#FF6B6B] transition-colors">Belgique@airbrs.com</p>
                  </div>
                </a>
                <a
                  href="mailto:Maroc@airbrs.com"
                  className="group flex items-center gap-3 bg-white/8 backdrop-blur-md border border-white/15 rounded-2xl px-6 py-4 hover:bg-white/15 hover:border-[#FF6B6B]/40 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#4ECDC4] to-[#2CB5AC] flex items-center justify-center shadow-lg">
                    <i className="ri-mail-line text-white"></i>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">{t('footer.contactEmailMorocco')}</p>
                    <p className="text-white font-medium text-sm group-hover:text-[#4ECDC4] transition-colors">Maroc@airbrs.com</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Links Section */}
        <div className="grid md:grid-cols-3 gap-8 mb-16 pb-16 border-b border-white/10">
          {/* Solutions */}
          <div>
            <h4 className="text-lg font-bold mb-5 flex items-center gap-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <i className="ri-tools-line text-[#FF6B6B]"></i>
              {t('footer.solutionsTitle')}
            </h4>
            <ul className="space-y-3">
              {solutions.map((item, i) => (
                <li key={i}>
                  <a href="#" className="text-white/70 hover:text-[#4ECDC4] transition-colors flex items-center gap-2 cursor-pointer group">
                    <i className="ri-arrow-right-s-line text-[#FF6B6B] group-hover:translate-x-1 transition-transform"></i>
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Entreprise */}
          <div>
            <h4 className="text-lg font-bold mb-5 flex items-center gap-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <i className="ri-building-line text-[#FF6B6B]"></i>
              {t('footer.companyTitle')}
            </h4>
            <ul className="space-y-3">
              {company.map((item, i) => (
                <li key={i}>
                  <a href="#" className="text-white/70 hover:text-[#4ECDC4] transition-colors flex items-center gap-2 cursor-pointer group">
                    <i className="ri-arrow-right-s-line text-[#FF6B6B] group-hover:translate-x-1 transition-transform"></i>
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Ressources */}
          <div>
            <h4 className="text-lg font-bold mb-5 flex items-center gap-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <i className="ri-file-text-line text-[#FF6B6B]"></i>
              {t('footer.resourcesTitle')}
            </h4>
            <ul className="space-y-3">
              {resources.map((item, i) => (
                <li key={i}>
                  <a href="#" className="text-white/70 hover:text-[#4ECDC4] transition-colors flex items-center gap-2 cursor-pointer group">
                    <i className="ri-arrow-right-s-line text-[#FF6B6B] group-hover:translate-x-1 transition-transform"></i>
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-6 text-sm text-white/50">
          <p>© 2025 Air Baggage Repair Solutions. {t('footer.copyright')}</p>
          <div className="flex gap-6">
            <a href="#" className="hover:text-[#FF6B6B] transition-colors cursor-pointer">
              {t('footer.privacy')}
            </a>
            <a href="#" className="hover:text-[#FF6B6B] transition-colors cursor-pointer">
              {t('footer.terms')}
            </a>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#FF6B6B] transition-colors cursor-pointer">
              {t('footer.poweredBy')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
